import { useState, useCallback } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import { ticketApi } from '../services/api.service';
import type { Ticket } from '../types/chat.types';

export interface UseTicketActionsOptions {
  onSuccess?: (ticket: Ticket) => void;
}

export interface UseTicketActionsReturn {
  isLoading: boolean;
  activeAction: string | null;
  close: (ticketId: number) => Promise<void>;
  reopen: (ticketId: number) => Promise<void>;
  returnToPending: (ticketId: number) => Promise<void>;
  transfer: (ticketId: number, data: { toUserId?: number; toQueueId?: number }) => Promise<void>;
  spy: (ticketId: number) => Promise<void>;
}

/**
 * useTicketActions - Hook for ticket action operations
 * Provides loading states and notification handling
 */
export function useTicketActions(options: UseTicketActionsOptions = {}): UseTicketActionsReturn {
  const { onSuccess } = options;
  const notify = useNotify();
  const refresh = useRefresh();

  const [activeAction, setActiveAction] = useState<string | null>(null);

  const handleAction = useCallback(async <T>(
    actionName: string,
    action: () => Promise<{ data: T }>,
    successKey: string,
    errorKey: string,
  ) => {
    setActiveAction(actionName);
    try {
      const result = await action();
      notify(successKey, { type: 'success' });
      onSuccess?.(result.data as Ticket);
      refresh();
    } catch {
      notify(errorKey, { type: 'error' });
    } finally {
      setActiveAction(null);
    }
  }, [notify, refresh, onSuccess]);

  const close = useCallback((ticketId: number) =>
    handleAction(
      'close',
      () => ticketApi.close(ticketId),
      'resources.ticket.notifications.closed',
      'resources.ticket.notifications.closeFailed',
    ), [handleAction]);

  const reopen = useCallback(async (ticketId: number) => {
    setActiveAction('reopen');
    try {
      const result = await ticketApi.reopen(ticketId);
      notify('resources.ticket.notifications.reopened', { type: 'success' });
      onSuccess?.(result.data as Ticket);
      refresh();
    } catch (error: any) {
      // Check if error is due to existing active ticket (409 Conflict)
      const isConflict = error?.response?.status === 409 || error?.status === 409;
      const errorMessage = error?.response?.data?.message || error?.message;

      if (isConflict && errorMessage?.includes('already has an active ticket')) {
        // Extract ticket number from error message if available
        const match = errorMessage.match(/ticket #(\d+)/);
        const existingTicketId = match ? match[1] : '';
        notify(
          `resources.ticket.notifications.reopenBlockedByActiveTicket`,
          {
            type: 'warning',
            messageArgs: { ticketId: existingTicketId }
          }
        );
      } else {
        notify('resources.ticket.notifications.reopenFailed', { type: 'error' });
      }
    } finally {
      setActiveAction(null);
    }
  }, [notify, refresh, onSuccess]);

  const returnToPending = useCallback((ticketId: number) =>
    handleAction(
      'return',
      () => ticketApi.returnToPending(ticketId),
      'resources.ticket.notifications.returnedToPending',
      'resources.ticket.notifications.returnFailed',
    ), [handleAction]);

  const transfer = useCallback((ticketId: number, data: { toUserId?: number; toQueueId?: number }) =>
    handleAction(
      'transfer',
      () => ticketApi.transfer(ticketId, data),
      'resources.ticket.notifications.transferred',
      'resources.ticket.notifications.transferFailed',
    ), [handleAction]);

  const spy = useCallback(async (ticketId: number) => {
    setActiveAction('spy');
    try {
      const result = await ticketApi.spy(ticketId);
      onSuccess?.(result.data as Ticket);
    } catch {
      notify('resources.ticket.notifications.spyFailed', { type: 'error' });
    } finally {
      setActiveAction(null);
    }
  }, [notify, onSuccess]);

  return {
    isLoading: activeAction !== null,
    activeAction,
    close,
    reopen,
    returnToPending,
    transfer,
    spy,
  };
}
